import { Card, Fade, Skeleton, useTheme } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";

import useCountUp from "../../hooks/use-count-up";
import { DashboardColor, pulseSx, surfaceCardSx } from "../styles";

type Props = {
  title: string;
  items: { name: string; count: number }[];
  emptyLabel: string;
  totalLabel: string;
  color?: DashboardColor;
  isLoading?: boolean;
  isRefreshing?: boolean;
};

const SEGMENT_COLORS: DashboardColor[] = ["primary", "info", "warning", "success", "error", "dark"];

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const DonutCard = ({
  title,
  items,
  emptyLabel,
  totalLabel,
  isLoading = false,
  isRefreshing = false,
}: Props) => {
  const theme = useTheme();
  const total = items.reduce((sum, item) => sum + item.count, 0);
  const animatedTotal = useCountUp(total);

  let offsetAccumulator = 0;

  return (
    <Card sx={surfaceCardSx("primary")}>
      <MDBox p={2}>
        <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
          {title}
        </MDTypography>
        {isLoading ? (
          <Skeleton variant="rounded" height="12rem" />
        ) : total === 0 ? (
          <MDTypography variant="button" color="text">
            {emptyLabel}
          </MDTypography>
        ) : (
          <Fade in timeout={520}>
            <MDBox
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
              sx={pulseSx(isRefreshing)}
            >
              <MDBox position="relative" width="8rem" height="8rem">
                <svg viewBox="0 0 140 140" width="100%" height="100%" role="img" aria-label={title}>
                  <g transform="rotate(-90 70 70)">
                    <circle
                      cx="70"
                      cy="70"
                      r={RADIUS}
                      fill="none"
                      stroke={theme.palette.grey[200]}
                      strokeWidth="18"
                    />
                    {items.map((item, index) => {
                      const fraction = item.count / total;
                      const dash = fraction * CIRCUMFERENCE;
                      const segment = (
                        <circle
                          key={item.name}
                          cx="70"
                          cy="70"
                          r={RADIUS}
                          fill="none"
                          stroke={
                            (theme.palette as any)[SEGMENT_COLORS[index % SEGMENT_COLORS.length]]?.main ??
                            theme.palette.primary.main
                          }
                          strokeWidth="18"
                          strokeLinecap="butt"
                          strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                          strokeDashoffset={-offsetAccumulator}
                          style={{ transition: "stroke-dasharray 1s ease, stroke-dashoffset 1s ease" }}
                        />
                      );
                      offsetAccumulator += dash;
                      return segment;
                    })}
                  </g>
                </svg>
                <MDBox
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MDTypography variant="h6" fontWeight="bold">
                    {new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(animatedTotal)}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {totalLabel}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox flex="1 1 8rem" minWidth="8rem">
                {items.map((item, index) => (
                  <MDBox key={item.name} display="flex" alignItems="center" gap={0.75} mb={0.4}>
                    <MDBox
                      variant="gradient"
                      bgColor={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                      width="0.6rem"
                      height="0.6rem"
                      borderRadius="sm"
                      sx={{ flexShrink: 0 }}
                    />
                    <MDTypography variant="caption" fontWeight="medium" sx={{ flex: 1 }}>
                      {item.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {item.count} (
                      {new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(
                        (item.count / total) * 100
                      )}
                      %)
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          </Fade>
        )}
      </MDBox>
    </Card>
  );
};

export default DonutCard;
